import React, { useState } from "react";
import { X, Trash2, Plus, Minus, CreditCard, ArrowLeft, ShoppingCart, Sparkles } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API_URL = `${BACKEND_URL}/api`;

export const CartDrawer = ({ isOpen, onClose, cart, updateQuantity, removeFromCart, clearCart }) => {
  const [step, setStep] = useState("cart"); // "cart" | "checkout" | "success"
  const [customer, setCustomer] = useState({ name: "", email: "", phone: "", address: "" });
  const [isSubmitting, setIsScubmitting] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [formError, setFormError] = useState("");

  if (!isOpen) return null;

  const totalAmount = cart.reduce((acc, item) => acc + item.price * item.quantity, 0);

  const handleInputChange = (e) => {
    setCustomer({ ...customer, [e.target.name]: e.target.value });
    setFormError("");
  };

  const handleCheckoutSubmit = async (e) => {
    e.preventDefault();
    if (!customer.name || !customer.email || !customer.phone || !customer.address) {
      setFormError("All fields are required to deliver the botanicals.");
      return;
    }

    setIsScubmitting(true);
    try {
      const orderPayload = {
        customer_name: customer.name,
        customer_email: customer.email,
        customer_phone: customer.phone,
        address: customer.address,
        items: cart.map(item => ({
          product_id: item.id,
          title: item.title,
          price: item.price,
          quantity: item.quantity,
          image: item.image
        })),
        total_amount: totalAmount
      };

      const response = await axios.post(`${API_URL}/orders`, orderPayload);
      setOrderResult(response.data);
      setStep("success");
      clearCart();
      setCustomer({ name: "", email: "", phone: "", address: "" });
    } catch (err) {
      console.error("Order submission failed:", err);
      setFormError("Unable to place simulated order. Please verify API connection.");
    } finally {
      setIsScubmitting(false);
    }
  };

  return (
    <div
      data-testid="cart-drawer-container"
      className="fixed inset-0 z-50 overflow-hidden font-outfit"
    >
      {/* Backdrop */}
      <div
        data-testid="cart-drawer-backdrop"
        onClick={onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-500"
      />

      <div className="absolute inset-y-0 right-0 max-w-full flex pl-10">
        <div
          data-testid="cart-drawer-content"
          className="w-screen max-w-md bg-[#F7F5F0] text-[#2B3A2F] flex flex-col shadow-2xl relative"
        >
          {/* Header */}
          <div className="p-6 border-b border-[#E2DACF] flex justify-between items-center bg-[#2B3A2F] text-[#F7F5F0]">
            <h2 className="text-xl font-light tracking-wider font-cormorant flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-[#C47D65]" />
              {step === "cart" && "Your Seedlings Basket"}
              {step === "checkout" && "Artisanal Shipping"}
              {step === "success" && "Woven with Love"}
            </h2>
            <button
              data-testid="cart-drawer-close-button"
              onClick={onClose}
              className="p-1 rounded-full hover:bg-white/10 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Body Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {step === "cart" && (
              <>
                {cart.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-64 text-center space-y-4">
                    <div className="w-16 h-16 rounded-full bg-[#E2DACF] flex items-center justify-center text-3xl">
                      🍂
                    </div>
                    <p className="text-[#5C6B5F] font-light">Your botanical basket is empty.</p>
                    <button
                      data-testid="cart-empty-shop-button"
                      onClick={onClose}
                      className="bg-[#2B3A2F] text-[#F7F5F0] px-6 py-2.5 hover:bg-[#1E2922] transition-all text-xs tracking-widest uppercase font-medium"
                    >
                      Browse Studio Pieces
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {cart.map((item) => (
                      <div
                        key={item.id}
                        data-testid={`cart-item-${item.id}`}
                        className="flex items-center space-x-4 bg-white p-3 border border-[#E2DACF]"
                      >
                        <img
                          data-testid={`cart-item-image-${item.id}`}
                          src={item.image}
                          alt={item.title}
                          className="w-16 h-16 object-cover bg-[#E2DACF]"
                        />
                        <div className="flex-1">
                          <h4
                            data-testid={`cart-item-title-${item.id}`}
                            className="font-cormorant font-normal text-base leading-tight"
                          >
                            {item.title}
                          </h4>
                          <p className="text-xs text-[#5C6B5F] font-light uppercase tracking-wider mb-2">
                            {item.category}
                          </p>
                          <p
                            data-testid={`cart-item-price-${item.id}`}
                            className="text-sm font-semibold"
                          >
                            ₹{item.price.toLocaleString("en-IN")}
                          </p>
                        </div>

                        {/* Quantity controls */}
                        <div className="flex flex-col items-center space-y-1">
                          <div className="flex items-center space-x-2 border border-[#E2DACF]">
                            <button
                              data-testid={`cart-item-decrement-${item.id}`}
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              className="p-1 hover:bg-[#E2DACF] transition-colors"
                            >
                              <Minus className="w-3 h-3" />
                            </button>
                            <span
                              data-testid={`cart-item-quantity-${item.id}`}
                              className="text-xs font-semibold px-1 min-w-[12px] text-center"
                            >
                              {item.quantity}
                            </span>
                            <button
                              data-testid={`cart-item-increment-${item.id}`}
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              className="p-1 hover:bg-[#E2DACF] transition-colors"
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                          <button
                            data-testid={`cart-item-remove-${item.id}`}
                            onClick={() => removeFromCart(item.id)}
                            className="text-red-500 hover:text-red-700 p-1 flex items-center gap-1 text-[10px] uppercase font-semibold"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {step === "checkout" && (
              <form
                data-testid="checkout-form"
                onSubmit={handleCheckoutSubmit}
                className="space-y-4"
              >
                {formError && (
                  <div
                    data-testid="checkout-error-message"
                    className="p-3 bg-red-50 border border-red-200 text-red-600 text-xs tracking-wide"
                  >
                    {formError}
                  </div>
                )}

                <div className="space-y-1">
                  <label className="text-xs uppercase tracking-widest text-[#5C6B5F] font-semibold">
                    Botanical Wearer Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    data-testid="checkout-name-input"
                    value={customer.name}
                    onChange={handleInputChange}
                    placeholder="E.g. Aditi Sharma"
                    className="w-full bg-white border border-[#E2DACF] px-4 py-2.5 text-sm focus:border-[#2B3A2F] outline-none"
                    required
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs uppercase tracking-widest text-[#5C6B5F] font-semibold">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    data-testid="checkout-email-input"
                    value={customer.email}
                    onChange={handleInputChange}
                    placeholder="E.g. aditi@gmail.com"
                    className="w-full bg-white border border-[#E2DACF] px-4 py-2.5 text-sm focus:border-[#2B3A2F] outline-none"
                    required
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs uppercase tracking-widest text-[#5C6B5F] font-semibold">
                    Mobile/Phone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    data-testid="checkout-phone-input"
                    value={customer.phone}
                    onChange={handleInputChange}
                    placeholder="E.g. +91 98765 43210"
                    className="w-full bg-white border border-[#E2DACF] px-4 py-2.5 text-sm focus:border-[#2B3A2F] outline-none"
                    required
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs uppercase tracking-widest text-[#5C6B5F] font-semibold">
                    Delivery Address
                  </label>
                  <textarea
                    name="address"
                    data-testid="checkout-address-input"
                    value={customer.address}
                    onChange={handleInputChange}
                    placeholder="Apartment, Street, Area, Bangalore, Pin"
                    rows="3"
                    className="w-full bg-white border border-[#E2DACF] px-4 py-2.5 text-sm focus:border-[#2B3A2F] outline-none"
                    required
                  />
                </div>

                <div className="p-4 bg-white border border-[#E2DACF] space-y-2 mt-6">
                  <p className="text-xs text-[#5C6B5F] uppercase tracking-wider font-semibold">
                    Payment Method
                  </p>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-green-700 font-bold">✓</span>
                    <span>Artisan Cash on Delivery / UPI on Reveal</span>
                  </div>
                  <p className="text-[10px] text-[#5C6B5F] leading-tight">
                    Since Kalorence clothes are handcrafted, unique eco-prints, we collect payment on simulated package arrival.
                  </p>
                </div>
              </form>
            )}

            {step === "success" && orderResult && (
              <div
                data-testid="checkout-success-container"
                className="text-center space-y-6 py-8"
              >
                <div className="w-20 h-20 rounded-full bg-[#2B3A2F] text-[#F7F5F0] flex items-center justify-center mx-auto text-4xl shadow-lg">
                  🍂
                </div>
                <div className="space-y-2">
                  <h3 className="text-2xl font-light font-cormorant text-[#2B3A2F]">
                    Order Placed Poetically
                  </h3>
                  <p className="text-sm text-[#5C6B5F] font-light leading-relaxed">
                    Thank you, {orderResult.customer_name}. We have carefully packed your one-of-a-kind leaf composition and started the shipment process from Jayanagar, Bangalore.
                  </p>
                </div>

                <div className="p-4 bg-white border border-[#E2DACF] text-left text-xs space-y-2 font-mono">
                  <div className="flex justify-between border-b border-[#E2DACF] pb-1.5">
                    <span>Simulated Order ID:</span>
                    <span data-testid="checkout-success-order-id" className="font-bold text-[#C47D65]">
                      {orderResult._id || orderResult.id}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Delivered To:</span>
                    <span>{orderResult.customer_email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total Amount:</span>
                    <span className="font-bold">₹{orderResult.total_amount.toLocaleString("en-IN")}</span>
                  </div>
                </div>

                <button
                  data-testid="checkout-success-continue-button"
                  onClick={() => {
                    setStep("cart");
                    onClose();
                  }}
                  className="w-full bg-[#2B3A2F] text-[#F7F5F0] py-3 hover:bg-[#1E2922] transition-all text-xs tracking-widest uppercase font-medium flex items-center justify-center gap-2"
                >
                  <Sparkles className="w-4 h-4" /> Continue Exploring Craft
                </button>
              </div>
            )}
          </div>

          {/* Footer Area */}
          {cart.length > 0 && step !== "success" && (
            <div className="p-6 border-t border-[#E2DACF] bg-white space-y-4">
              <div className="flex justify-between items-baseline">
                <span className="text-sm text-[#5C6B5F] font-light">Subtotal</span>
                <span
                  data-testid="cart-total-price"
                  className="text-xl font-semibold tracking-tight"
                >
                  ₹{totalAmount.toLocaleString("en-IN")}
                </span>
              </div>

              {step === "cart" && (
                <button
                  data-testid="cart-checkout-button"
                  onClick={() => setStep("checkout")}
                  className="w-full bg-[#2B3A2F] text-[#F7F5F0] py-3.5 hover:bg-[#1E2922] transition-all text-xs tracking-widest uppercase font-medium flex items-center justify-center gap-2 shadow-md"
                >
                  <CreditCard className="w-4 h-4" /> Proceed to Artisan Shipping
                </button>
              )}

              {step === "checkout" && (
                <div className="flex gap-4">
                  <button
                    data-testid="checkout-back-button"
                    onClick={() => setStep("cart")}
                    className="flex-1 border border-[#2B3A2F] text-[#2B3A2F] py-3 hover:bg-[#E2DACF] transition-all text-xs tracking-widest uppercase font-medium flex items-center justify-center gap-1"
                  >
                    <ArrowLeft className="w-4 h-4" /> Basket
                  </button>
                  <button
                    data-testid="checkout-submit-button"
                    onClick={handleCheckoutSubmit}
                    disabled={isSubmitting}
                    className="flex-1 bg-[#C47D65] text-white py-3 hover:bg-[#b06752] transition-all text-xs tracking-widest uppercase font-medium flex items-center justify-center gap-1 disabled:opacity-50"
                  >
                    {isSubmitting ? "Creating..." : "Confirm Wear"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
